import React, { useEffect, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Pagination from '../Pagination';
import Spinner from '../ui/Spinner';
import { getExpenseApprovals, getDriverExpenses } from '../../services/driver_expenses';
import { formatDMY } from '../../utils/datetime';

const DriverExpenseApprovalTable = ({
  month,
  year,
  status,
  selectedDriver,
  onCountUpdate,
  onApprove,
  onReject,
  reload,
}) => {
  const [approvals, setApprovals] = useState([]);
  const [approvalCount, setApprovalCount] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const approvalsPerPage = 10;

  useEffect(() => {
    if (selectedDriver) {
      fetchData(1);
      setCurrentPage(1);
    }
  }, [month, year, status, selectedDriver, reload]);

  const fetchData = async page => {
    setLoading(true);
    await Promise.all([fetchExpenses(1), fetchApprovals(page)]);
    setLoading(false);
  };

  const fetchExpenses = async page => {
    setLoading(true);
    const [err, data] = await getDriverExpenses({
      driver: selectedDriver,
      status,
      month,
      year,
      page,
    });
    if (!err) {
      onCountUpdate('expenses', data.count);
    }
    setLoading(false);
  };

  const fetchApprovals = async page => {
    setLoading(true);
    const [err, data] = await getExpenseApprovals({
      driver: selectedDriver,
      status,
      month,
      year,
      page,
    });
    if (!err) {
      setApprovals(data.results);
      setApprovalCount(data.count);
      setCurrentPage(page || 1);
      onCountUpdate('approvals', data.count);
    }
    setLoading(false);
  };

  return (
    <>
      <div className="overflow-x-auto mt-4">
        <table className="min-w-full bg-white border">
          <thead className="bg-[#293686] text-white uppercase text-sm">
            <tr>
              <th className="px-4 py-2 border text-center">Date</th>
              <th className="px-4 py-2 border text-center">Charges</th>
              <th className="px-4 py-2 border text-center">Amount</th>
              <th className="px-4 py-2 border text-center">Attachment</th>
              <th className="px-4 py-2 border text-center">Created By</th>
              <th className="px-4 py-2 border text-center">Requested By</th>
              <th className="px-4 py-2 border text-center">Approval</th>
              <th className="px-4 py-2 border text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  <Spinner />
                </td>
              </tr>
            ) : approvals.length > 0 ? (
              approvals.map(item => (
                <tr key={item.id} className="text-sm text-gray-800">
                  <td className="px-4 py-2 border text-center">{formatDMY(item.date)}</td>
                  <td className="px-4 py-2 border text-center">
                    {item.expense_type?.toUpperCase()}
                  </td>
                  <td className="px-4 py-2 border text-center">₹{item.amount}</td>
                  <td className="px-4 py-2 border text-center">
                    {item.supporting_doc ? (
                      <a
                        href={item.supporting_doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        VIEW
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-2 border text-center">
                    {item.created_by?.name.toUpperCase()}
                  </td>
                  <td className="px-4 py-2 border text-center">
                    {item.updated_by?.name.toUpperCase()}
                  </td>
                  <td className="px-4 py-2 border text-center">
                    {item.approval_reason_display?.toUpperCase()}
                  </td>
                  <td className="px-4 py-2 border text-center">
                    {item.can_approve && (
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => onApprove(item)}
                          className="bg-green-100 hover:bg-green-200 text-green-700 px-2 py-1 rounded"
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => onReject(item)}
                          className="bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-gray-500 py-3">
                  NO PENDING APPROVALS FOUND.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {approvals.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.max(1, Math.ceil(approvalCount / approvalsPerPage))}
          onPageChange={setPendingPage}
        />
      )}
    </>
  );
};

export default DriverExpenseApprovalTable;
